import { useGetProjectLabelsQuery } from "@/modules/project/services/projectApi";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { EvalLimitDetail, type Label } from "@repo/schema";
import { useTranslation } from "react-i18next";
import { EvalActionsSection } from "../EvalActions";
import { GeneralSection } from "./GeneralSection";
import { LimitsSection } from "./LimitsSection";
import { SetupSection } from "./SetupSection";
import { useLimitForm } from "./useLimitForm.hook";

export type UpdateLimitModalProps = {
  projectId: number;
  configId: number;
  testCaseId: string;
  limit: EvalLimitDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const UpdateLimitModal = ({
  projectId,
  configId,
  testCaseId,
  limit,
  open,
  onOpenChange,
}: UpdateLimitModalProps) => {
  const { t } = useTranslation(["evaluation", "common"]);
  const { form, isSubmitting } = useLimitForm({
    projectId,
    configId,
    testCaseId,
    limitId: limit.id,
    initialValues: limit,
    onSuccess: () => onOpenChange(false),
  });

  const { data: labels = [] } = useGetProjectLabelsQuery({ projectId });
  // Pickers offer active labels only. The limit's own target / parent stays
  // selectable if it has been archived since, so the form shows (and can keep)
  // what is deployed instead of going blank.
  const archivedCurrent = [limit.targetLabel, limit.targetParentLabel].filter(
    (l, i, all): l is Label =>
      !!l?.deletedAt && all.findIndex((o) => o?.id === l.id) === i,
  );
  const labelOptions = [
    ...labels.map((l) => ({ label: l.name, value: String(l.id) })),
    ...archivedCurrent.map((l) => ({
      label: t("limitForm.archivedLabelOption", { name: l.name }),
      value: String(l.id),
    })),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t("limitForm.editTitle")}
          </DialogTitle>
        </DialogHeader>

        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-8"
          >
            <GeneralSection />
            <SetupSection labelOptions={[...labelOptions]} />
            <LimitsSection labelOptions={labelOptions} />
            <EvalActionsSection />

            <DialogFooter>
              <DialogClose render={<Button type="button" variant="outline" />}>
                {t("common:actions.cancel")}
              </DialogClose>
              <Button type="submit" disabled={isSubmitting}>
                {t("common:actions.save")}
              </Button>
            </DialogFooter>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
};
